import React, { useEffect, useState, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './ChallengeAI.module.css';
import globalStyles from '../../App.module.css';
import Board from '../Board';
import { GameContext } from '../Providers/GameContext';
import { useTheme } from '../Providers/ThemeContext';
import { useLanguage } from '../Providers/LanguageContext';
import { AuthContext } from '../Providers/AuthContext';
import GlobalHeader from '../GlobalHeader';
import AdSenseLayout from '../AdSense/AdSenseLayout';
import WispyStars from '../Tutorials/WispyStars';
import ZodiacEngineService from '../ZodiacEngineService';
import { PieceType, State, GameState, Mode } from '../../services/zodiacEngine/types';
import FENHistory from '../../services/zodiacEngine/Fen/FENHistory';
import GameInfo from '../GameInfo/GameInfo';
import TurnPopover from '../TurnPopover/TurnPopover';
import { createAIOrchestrator, AIPlayerType } from '../../services/zodiacEngine/AI';
import { BattleReporter } from '../../services/zodiacEngine/AI/Analysis/BattleReporter';
import KingSelectionModal from '../KingSelection/KingSelectionModal';
import { Piece } from '../../services/zodiacEngine/Piece';
import { allPieceTypes } from '../../services/zodiacEngine/const';
import { PieceSelectionPanel } from '../shared/PieceSelectionPanel';
import sharedStyles from '../shared/PieceSelection.module.css';
import {
  AIVariant,
  AIModel,
  AI_VARIANT_CONFIG,
  AI_MODEL_CONFIG,
  getBaseAIType,
  getTimeLimit,
  getAIGridLayout,
  getRowLabels,
  getColumnLabels,
  getBaseAITypeFromModel,
  modelAndTimeToVariant,
  variantToModelAndTime,
} from '../../services/zodiacEngine/AI/Core/AIVariants';
import { MinimaxModel, MinimaxAI, PieceSelectionReasoning } from '../../services/zodiacEngine/AI/MinimaxAI';
import { AIMoveResult } from '../../services/zodiacEngine/AI/Core/IAIPlayer';
// AIEvaluationPanel hidden for now
// import AIEvaluationPanel from './AIEvaluationPanel';
import { TimeControlPreset, TimeControlManager, getPresetConfig, getPresetDisplayName, getPresetDescription, isTimedPreset } from '../../services/timeControl';
import TimerDisplay from '../TimeControl/TimerDisplay';
import GameEndPopup, { WinCondition } from './GameEndPopup';
import { TimeFrame, TIME_FRAME_CONFIG, getTimeLimitFromFrame } from './ThinkingTime';
import { Difficulty, DIFFICULTY_TO_MODEL, modelToDifficulty, getDifficultyLabel } from './Difficulty';
import NewPlayerWelcomeModal from './NewPlayerWelcomeModal';
import AIThinking from '../MCTSStats/AIThinking';
import { PieceStatsCollector } from '../../services/bots/pieceStats';
import { playUISound, playVictorySound, playDefeatSound } from '../../utils/UISoundEffects';
import AstraReview from './AstraReview';
import { AstraReportRecorder, AstraGameReport, loadLatestAstraReport } from '../../services/zodiacEngine/AI/AstraAI/AstraReport';
import { copyState } from '../../services/zodiacEngine/AI/AstraAI/AstraRules';
import { AstraSeries, saveSeries, loadSeries, downloadSeries } from '../../services/zodiacEngine/AI/AstraAI/AstraSeries';

function ChallengeAI() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const { user, userProfile } = useContext(AuthContext);
  const {
    gameState,
    potentialMoves,
    potentialSideEffects,
    updateGameState
  } = useContext(GameContext);

  const seriesRequested = new URLSearchParams(location.search).get('playtest') === 'blind';
  const [seriesActive, setSeriesActive] = useState(seriesRequested);
  const [series, setSeries] = useState<AstraSeries>(() => ({ id: localStorage.getItem('zodiac_latest_series') || 'blind-series-' + crypto.randomUUID(), startedAt: new Date().toISOString(), games: [] }));
  const [seriesReady, setSeriesReady] = useState(false);
  const [seriesMessage, setSeriesMessage] = useState('Preparing recording...');
  const seriesStarted = useRef(false);
  const seriesRecorded = useRef(new Set<string>());
  const seriesTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seriesActiveRef = useRef(seriesActive);
  seriesActiveRef.current = seriesActive;
  useEffect(() => {
    if (!seriesRequested) return;
    let cancelled = false;
    loadSeries(series.id).then(saved => {
      if (cancelled) return;
      if (saved) { setSeries(saved); saved.games.forEach(g => seriesRecorded.current.add(g.id)); }
      localStorage.setItem('zodiac_latest_series', series.id);
      setSeriesReady(true); setSeriesMessage('You: Light | ZSTR Blind: Dark | Deep 20s | No clock');
    }).catch(() => { setSeriesActive(false); setSeriesMessage('Recording storage unavailable. Series has not started.'); });
    return () => { cancelled = true; seriesActiveRef.current = false; if (seriesTimer.current) clearTimeout(seriesTimer.current); };
  }, []);

  // NEW SYSTEM: Separate model and time frame selection
  // Load saved preferences from localStorage
  const getInitialPreferences = () => {
    if (seriesRequested) return { lightModel: null, darkModel: AIModel.ZSTR, lightBlind: false, darkBlind: true,
      lightTimeFrame: TimeFrame.TWENTY, darkTimeFrame: TimeFrame.TWENTY, timeControlPreset: TimeControlPreset.OFF, randomStart: false };
    try {
      const saved = localStorage.getItem('challengeAI_preferences');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
    }
    return null;
  };
  const savedPrefs = getInitialPreferences();

  // Preserve current model choices, including Zodiac Astra.
  const normalizeSavedModel = (saved: AIModel | null | undefined, fallback: AIModel | null): AIModel | null => {
    if (saved === AIModel.ZSTR_BLIND) return AIModel.ZSTR;
    if (saved === null) return null;
    if (saved === undefined) return fallback;
    if (saved === AIModel.ZSTR || saved === AIModel.ALCHEMIST || saved === AIModel.CREATURE || saved === AIModel.SENTINEL) return saved;
    return AIModel.SENTINEL;
  };

  const [lightModel, setLightModel] = useState<AIModel | null>(
    normalizeSavedModel(savedPrefs?.lightModel, null)
  );
  const [darkModel, setDarkModel] = useState<AIModel | null>(
    normalizeSavedModel(savedPrefs?.darkModel, AIModel.CREATURE)
  );
  const [lightDifficulty, setLightDifficulty] = useState<Difficulty>(
    modelToDifficulty(normalizeSavedModel(savedPrefs?.lightModel, AIModel.CREATURE))
  );
  const [darkDifficulty, setDarkDifficulty] = useState<Difficulty>(
    modelToDifficulty(normalizeSavedModel(savedPrefs?.darkModel, AIModel.CREATURE))
  );
  const [lightBlind, setLightBlind] = useState<boolean>(savedPrefs?.lightBlind ?? savedPrefs?.lightModel === AIModel.ZSTR_BLIND);
  const [darkBlind, setDarkBlind] = useState<boolean>(savedPrefs?.darkBlind ?? savedPrefs?.darkModel === AIModel.ZSTR_BLIND);
  const reportModel = (model: AIModel | null, blind: boolean) => model === AIModel.ZSTR && blind ? AIModel.ZSTR_BLIND : model || 'player';
  const [lightTimeFrame, setLightTimeFrame] = useState<TimeFrame>(
    savedPrefs?.lightTimeFrame || TimeFrame.STANDARD
  );
  const [darkTimeFrame, setDarkTimeFrame] = useState<TimeFrame>(
    savedPrefs?.darkTimeFrame || TimeFrame.STANDARD
  );

  // Time control settings (defaults to STANDARD)
  const [timeControlPreset, setTimeControlPreset] = useState<TimeControlPreset>(
    savedPrefs?.timeControlPreset || TimeControlPreset.STANDARD
  );
  // Preset actually applied to the in-progress game. Diverges from the saved
  // preference when the welcome flow forces OFF (untimed first match).
  const [activePreset, setActivePreset] = useState<TimeControlPreset>(
    savedPrefs?.timeControlPreset || TimeControlPreset.STANDARD
  );

  // RANDOM START — a remembered preference, not a second button.
  //
  // There used to be two buttons, Start and Start Random, which made the random opening a
  // separate way to begin rather than a property of the game you want. The toggle folds it
  // into one: Start deals whichever opening this is set to, and PLAY NOW on the main page
  // reads the same flag, so a player who likes rostered games gets king and piece selection
  // in one click and a player who likes quick ones gets a dealt army.
  //
  // Defaults ON so nothing changes for anyone who has not touched it — a fresh install and
  // every existing player carry on getting the random opening they had before.
  const [randomStart, setRandomStart] = useState<boolean>(savedPrefs?.randomStart ?? true);

  const forceUntimedRef = useRef<boolean>(false);
  // What the quick start is waiting for the panel to settle on before it deals the game.
  // Null outside a quick start. See the PLAY NOW effect below.
  const quickStartTargetRef = useRef<{ light: AIModel | null; dark: AIModel | null; darkTimeFrame: TimeFrame; random: boolean } | null>(null);

  // Derived values
  const lightPlayer = lightModel === null ? AIPlayerType.PLAYER : getBaseAITypeFromModel(lightModel);
  const darkPlayer = darkModel === null ? AIPlayerType.PLAYER : getBaseAITypeFromModel(darkModel);
  const lightTimeLimit = getTimeLimitFromFrame(lightTimeFrame);
  const darkTimeLimit = getTimeLimitFromFrame(darkTimeFrame);

  const hasAstra = [lightModel,darkModel].some(model=>model===AIModel.ZSTR || model===AIModel.ZSTR_BLIND);
  const astraRecorder = useRef(new AstraReportRecorder());
  const [astraReport, setAstraReport] = useState<AstraGameReport | null>(loadLatestAstraReport);

  // Game state
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [isAIThinking, setIsAIThinking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isPauseQueued, setIsPauseQueued] = useState(false);
  const [showKings, setShowKings] = useState(false); // For AI vs AI games - toggle king visibility
  const [showKingsQueued, setShowKingsQueued] = useState<boolean | null>(null); // Queued showKings value
  const [aiOrchestratorRef] = useState(() => createAIOrchestrator(ZodiacEngineService.zodiacEngine));
  const [battleReporter] = useState(() => new BattleReporter());
  const [lastAIMoveResult, setLastAIMoveResult] = useState<AIMoveResult | null>(null);
  const [pieceSelectionReasonings, setPieceSelectionReasonings] = useState<PieceSelectionReasoning[]>([]);

  // Selection mode state
  type SelectionMode = 'None' | 'KingSelection' | 'PieceSelection';
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('None');
  const [isSelectingForLight, setIsSelectingForLight] = useState(true); // Which player is currently selecting
  const [selectedLightKing, setSelectedLightKing] = useState<PieceType | null>(null);
  const [selectedDarkKing, setSelectedDarkKing] = useState<PieceType | null>(null);

  const fenHistoryRef = useRef<any>(null);
  const challengeConfigRef = useRef<{lightPlayer: AIPlayerType, darkPlayer: AIPlayerType, showKings: boolean} | null>(null);
  const [moveHistoryResetKey, setMoveHistoryResetKey] = useState(0);
  const [placedPieces, setPlacedPieces] = useState<Set<string>>(new Set());
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const [isLightPieceTurn, setIsLightPieceTurn] = useState(true); // Track whose turn it is to place pieces
  const [lightKingPlacementOrder, setLightKingPlacementOrder] = useState<number>(1); // Random 1-5
  const [darkKingPlacementOrder, setDarkKingPlacementOrder] = useState<number>(1); // Random 1-5

  // Mobile-specific state
  const [isMobileGamePage, setIsMobileGamePage] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedPieceForPlacement, setSelectedPieceForPlacement] = useState<{type: PieceType, isLight: boolean} | null>(null);
  const [showMobileKingSelection, setShowMobileKingSelection] = useState(false);
  const [showMobileHistory, setShowMobileHistory] = useState(false);

  // Image error state for model selection (needs to be at top level for hooks)
  const [imageErrors, setImageErrors] = useState<Set<AIModel>>(new Set());

  // Game end popup state
  const [showGameEndPopup, setShowGameEndPopup] = useState(false);
  const [gameEndResult, setGameEndResult] = useState<'won' | 'lost' | 'draw'>('won');
  const [gameEndCondition, setGameEndCondition] = useState<WinCondition>('kingslayer');
  const [gameEndRaceKingType, setGameEndRaceKingType] = useState<string | undefined>(undefined);
  const [gameEndCapturedKingType, setGameEndCapturedKingType] = useState<PieceType | undefined>(undefined);
  const [gameEndCapturedKingIsLight, setGameEndCapturedKingIsLight] = useState<boolean | undefined>(undefined);
  const [pendingRematch, setPendingRematch] = useState(false);
  const hasShownGameEndPopupRef = useRef(false); // Prevent re-showing after close

  // AI failure tracking to prevent infinite loops
  const [consecutiveAIFailures, setConsecutiveAIFailures] = useState(0);
  const MAX_AI_FAILURES = 3;

  // New-player welcome flow (triggered from MainPage PLAY NOW button)
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [pendingNewPlayerStart, setPendingNewPlayerStart] = useState(false);
  const newPlayerProcessed = useRef(false);

  // Replay takeover: auto-start game from a FEN position passed via router state
  const replayTakeoverProcessed = useRef(false);
  useEffect(() => {
    const takeover = (location.state as any)?.replayTakeover;
    if (!takeover || replayTakeoverProcessed.current) return;
    replayTakeoverProcessed.current = true;

    const { fen, lightKing, darkKing, playerSide, aiModel: requestedModel, aiTimeFrame } = takeover;
    const aiModel = requestedModel === AIModel.ZSTR_BLIND ? AIModel.ZSTR : requestedModel;
    const takeoverBlind = requestedModel === AIModel.ZSTR_BLIND || (playerSide === 'light' ? darkBlind : lightBlind);
    if (playerSide === 'light') setDarkBlind(takeoverBlind); else setLightBlind(takeoverBlind);

    // Configure AI sides
    if (playerSide === 'light') {
      setLightModel(null); // Player
      setDarkModel(aiModel);
      setDarkDifficulty(modelToDifficulty(aiModel));
      setDarkTimeFrame(aiTimeFrame || TimeFrame.STANDARD);
    } else {
      setDarkModel(null); // Player
      setLightModel(aiModel);
      setLightDifficulty(modelToDifficulty(aiModel));
      setLightTimeFrame(aiTimeFrame || TimeFrame.STANDARD);
    }

    // Short delay to let state settle, then start the game
    setTimeout(() => {
      const engine = ZodiacEngineService.zodiacEngine;
      const playerIdForLight = user?.uid || 'challenge-ai-player';
      astraRecorder.current.start(playerSide === 'light' ? 'player' : reportModel(aiModel, takeoverBlind),
        playerSide === 'dark' ? 'player' : reportModel(aiModel, takeoverBlind),
        getTimeLimitFromFrame(aiTimeFrame || TimeFrame.STANDARD), getTimeLimitFromFrame(aiTimeFrame || TimeFrame.STANDARD));
      setAstraReport(null);
      aiOrchestratorRef.resetAIs();

      // Load FEN to get the board
      engine.loadFromFEN(fen);
      const loadedBoard = engine.getGameState().board;

      // Start game with loaded board
      const lk = lightKing as PieceType;
      const dk = darkKing as PieceType;
      engine.switchToTestAIMode(playerIdForLight, lk, dk, loadedBoard);

      const newGameState = engine.getGameState();
      newGameState.state = State.Playing;

      // Set challenge config for AI orchestrator
      const effectiveLightPlayer = playerSide === 'light' ? AIPlayerType.PLAYER : getBaseAITypeFromModel(aiModel);
      const effectiveDarkPlayer = playerSide === 'dark' ? AIPlayerType.PLAYER : getBaseAITypeFromModel(aiModel);
      challengeConfigRef.current = {
        lightPlayer: effectiveLightPlayer,
        darkPlayer: effectiveDarkPlayer,
        showKings: false,
      };
      (newGameState as any).challengeAIConfig = { ...challengeConfigRef.current };
      // getGameState returns a shallow copy; publish the UI config explicitly
      // and retain it across subsequent human/AI updates, as normal starts do.
      updateGameState(newGameState);

      // Configure the AI orchestrator
      aiOrchestratorRef.setLightPlayer(effectiveLightPlayer, takeoverBlind);
      aiOrchestratorRef.setDarkPlayer(effectiveDarkPlayer, takeoverBlind);

      engine.getEventManager().notifyLocalListeners();

      // Initialize time control for takeover games (same as normal game start)
      setActivePreset(timeControlPreset);
      const timeConfig = getPresetConfig(timeControlPreset);
      TimeControlManager.initialize(timeConfig, 'local');
      TimeControlManager.startTimer('light');

      setIsGameStarted(true);
      setSelectionMode('None');

      // Clear the router state to prevent re-triggering on re-render
      window.history.replaceState({}, document.title);
    }, 100);
  }, [location.state, user]);

  // PLAY NOW arrives here: straight into a game, on the settings you last played.
  //
  // TWO CASES, and the difference is whether the player has been here before.
  //   First game ever  — the beginner roster (weakest AI, no clock). Nothing is saved yet,
  //                      and a first match shouldn't open on someone else's Deep Sentinel.
  //   Every time after — whatever the panel already restored from challengeAI_preferences,
  //                      untouched. A regular on Hard/Blitz gets Hard/Blitz in one click;
  //                      forcing beginner here would also have OVERWRITTEN that saved
  //                      config, because the preferences effect persists whatever is set.
  //
  // (The game start is deliberately not tied to the welcome card. It used to be one effect
  // that bailed out early once the card had been seen, so every PLAY NOW after the first
  // dropped the player on the setup screen with nothing happening.)
  useEffect(() => {
    const nav = location.state as any;
    if (!nav?.quickStart || newPlayerProcessed.current) return;
    newPlayerProcessed.current = true;

    // First press only: the welcome card over the top of the board. Written as seen the
    // moment it's shown. ?welcome=1 forces it back when retesting the copy.
    const forceWelcome = new URLSearchParams(window.location.search).get('welcome') === '1';
    const firstGame =
      nav.newPlayerWelcome && (forceWelcome || localStorage.getItem('zodiacWelcomeSeen') !== 'true');
    if (firstGame) {
      // The welcome card over the top of the board, written as seen the moment it shows.
      setShowWelcomeModal(true);
      localStorage.setItem('zodiacWelcomeSeen', 'true');
      // The beginner roster: player as light, the weakest AI opposite, kings at random...
      setLightModel(null);
      setDarkModel(AIModel.ALCHEMIST);
      // ...the panel's difficulty label kept in step with the model it describes...
      setDarkDifficulty(Difficulty.EASY);
      setDarkTimeFrame(TimeFrame.FAST);
      // ...and no clock. A first match is for learning the board, not managing time. Read
      // at game-init and cleared after, so the saved preference isn't polluted.
      forceUntimedRef.current = true;
      // Forced random regardless of the toggle: the welcome card promises "no king or piece
      // selection", and a first-ever game should not open on a placement screen.
      quickStartTargetRef.current = { light: null, dark: AIModel.ALCHEMIST, darkTimeFrame: TimeFrame.FAST, random: true };
    } else {
      // Keep the restored preferences as they are — the ONE exception being a saved
      // AI-vs-AI setup, where nobody is holding a side. PLAY NOW means the player plays,
      // so light becomes theirs; a saved "play as dark" (light is an AI, dark is null) is
      // a real preference and survives.
      const nobodyIsPlaying = lightModel !== null && darkModel !== null;
      if (nobodyIsPlaying) setLightModel(null);
      quickStartTargetRef.current = {
        light: nobodyIsPlaying ? null : lightModel,
        dark: darkModel,
        darkTimeFrame,
        // ...and the opening the player has chosen for every game they start.
        random: randomStart,
      };
    }

    window.history.replaceState({}, document.title);
    setPendingNewPlayerStart(true);
  }, [location.state]);

  // Fires once the state-setters above have committed, then starts the game.
  // Two-effect dance is needed because handleStartGame closes over lightPlayer/darkPlayer/
  // darkTimeLimit derived from these state values — it has to run on the render where they
  // are the values the game is meant to start with, which is what the target ref records.
  useEffect(() => {
    if (!pendingNewPlayerStart) return;
    const target = quickStartTargetRef.current;
    if (target) {
      if (lightModel !== target.light) return;
      if (darkModel !== target.dark) return;
      if (darkTimeFrame !== target.darkTimeFrame) return;
    }
    const useRandom = target ? target.random : randomStart;
    setPendingNewPlayerStart(false);
    quickStartTargetRef.current = null;
    if (useRandom) handleStartGame();
    else handleStartWithSelection();  // PLAY NOW with the toggle off opens king/piece selection
  }, [pendingNewPlayerStart, lightModel, darkModel, darkTimeFrame]);

  // Detect mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Save AI preferences to localStorage when they change
  useEffect(() => {
    try {
      const preferences = {
        lightBlind,
        darkBlind,
        lightModel,
        darkModel,
        lightTimeFrame,
        darkTimeFrame,
        lightDifficulty,
        darkDifficulty,
        timeControlPreset,
        randomStart,
      };
      localStorage.setItem('challengeAI_preferences', JSON.stringify(preferences));
    } catch (e) {
    }
  }, [lightModel, darkModel, lightTimeFrame, darkTimeFrame, lightDifficulty, darkDifficulty, timeControlPreset, randomStart, lightBlind, darkBlind]);

  // Clear the board when entering ChallengeAI page to prevent stale pieces from previous games
  useEffect(() => {
  
    const gameStateManager = (ZodiacEngineService.zodiacEngine as any).gameStateManager;
    gameStateManager.emptyBoard();
    gameStateManager.setMode(Mode.TestAI);
    gameStateManager.setGameStateState(State.KingSelection);

    // Reset game state properties
    const freshGameState = gameStateManager.getGameState();
    freshGameState.fenHistory = ['8/8/8/8/8/8/8/8'];
    (freshGameState as any).repeatString = "";
    freshGameState.lightKing = null;
    freshGameState.darkKing = null;
    freshGameState.lightWon = undefined;
    freshGameState.isLightTurn = true;

    // Update the context
    updateGameState(freshGameState);
  }, []);

  const handleHome = () => {
    navigate('/');
  };

  const handleMobileBack = () => {
    setIsMobileGamePage(false);
    handleResetGame();
  };

  // Mobile square click handler for piece placement
  const handleMobileSquareClick = (position: { row: number; col: number }) => {
  

    if (!isMobile || selectionMode !== 'PieceSelection' || !selectedPieceForPlacement) {
    
      return;
    }

    const { type, isLight } = selectedPieceForPlacement;
  

    // Check if it's this player's turn to place
    if (isLight !== isLightPieceTurn) {
     
      setWarningMessage(isLightPieceTurn ? t('challengeAI.warningLightTurn') : t('challengeAI.warningDarkTurn'));
      return;
    }

    // Validate placement row
    const requiredRow = isLight ? 0 : 7;
    if (position.row !== requiredRow) {
    
      setWarningMessage(isLight ? t('challengeAI.warningLightRow') : t('challengeAI.warningDarkRow'));
      return;
    }

    // Check if the square is already occupied
    const currentGameState = ZodiacEngineService.zodiacEngine.getGameState();
    const square = currentGameState.board[position.row][position.col];
    if (square.Pieces.length > 0) {
     
      setWarningMessage(t('challengeAI.warningOccupied'));
      return;
    }

    // Check if piece is already on the board
    const pieceKey = `${type}-${isLight ? 'light' : 'dark'}`;
    if (placedPieces.has(pieceKey)) {
    
      return;
    }

    // Count how many pieces of this color are already placed
    const placedCount = Array.from(placedPieces).filter(key =>
      key.endsWith(isLight ? '-light' : '-dark')
    ).length;

    // Check if this is the 5th piece and king hasn't been placed yet
    const kingType = isLight ? selectedLightKing : selectedDarkKing;
    const kingKey = `${kingType}-${isLight ? 'light' : 'dark'}`;
    const kingPlaced = placedPieces.has(kingKey);
    const isLocalPvP = lightPlayer === AIPlayerType.PLAYER && darkPlayer === AIPlayerType.PLAYER;

    if (placedCount === 4 && !kingPlaced && type !== kingType) {
      // In local PvP, silently reject to avoid revealing king identity
      // In other modes, show warning modal
      if (!isLocalPvP) {
        setWarningMessage(t('challengeAI.warningKingRequired').replace('{kingType}', kingType || ''));
      }
      return;
    }

    try {
      const piece = new Piece(type as PieceType, isLight);

      // Set penguin direction
      if (type === 'Penguin') {
        piece.direction = isLight ? 'down' : 'up';
      }

      // Mark as king if this is the selected king piece
      if (type === kingType) {
        piece.isKing = true;
      }

      ZodiacEngineService.zodiacEngine.placePiece(piece, position);

      // Mark piece as placed
      setPlacedPieces(prev => new Set(prev).add(pieceKey));

      // Clear selection
      setSelectedPieceForPlacement(null);

      // Play sound
      const audio = new Audio('/turn change.mp3');
      audio.volume = 0.3;
      audio.play().catch(console.error);

      // Switch turns
      setIsLightPieceTurn(!isLightPieceTurn);

    } catch (error) {
    }
  };

  // Helper function to update game state while preserving challengeAIConfig
  const updateGameStateWithConfig = (newGameState: GameState) => {
    // Always re-attach the challenge config if we're in a challenge game
    if (challengeConfigRef.current) {
      (newGameState as any).challengeAIConfig = {
        ...challengeConfigRef.current,
      };
    
    }
    updateGameState(newGameState);
  };

  // Configure AI players
  useEffect(() => {
   
    aiOrchestratorRef.setLightPlayer(lightPlayer, lightBlind);
    aiOrchestratorRef.setDarkPlayer(darkPlayer, darkBlind);

    // Configure minimax models if using minimax-based AIs
    if (lightModel !== null && lightPlayer === AIPlayerType.MINIMAX) {
      const modelConfig = AI_MODEL_CONFIG[lightModel];
     
      if (modelConfig.minimaxModel) {
        const minimaxAI = aiOrchestratorRef.getAI(AIPlayerType.MINIMAX) as MinimaxAI;
       
        if (minimaxAI && minimaxAI.setModel) {
          minimaxAI.setModel(modelConfig.minimaxModel);
        
        } else {
        }
      }
    }

    if (darkModel !== null && darkPlayer === AIPlayerType.MINIMAX) {
      const modelConfig = AI_MODEL_CONFIG[darkModel];
    
      if (modelConfig.minimaxModel) {
        const minimaxAI = aiOrchestratorRef.getAI(AIPlayerType.MINIMAX) as MinimaxAI;
      
        if (minimaxAI && minimaxAI.setModel) {
          minimaxAI.setModel(modelConfig.minimaxModel);
         
        } else {
        }
      }
    }

    // Sync orchestrator + time limits to engine so AIScheduler's
    // engine.makeAIMove() uses the correct AI model and time limits
    ZodiacEngineService.zodiacEngine.aiOrchestrator = aiOrchestratorRef;
    ZodiacEngineService.zodiacEngine.aiTimeLimits = {
      light: lightTimeLimit,
      dark: darkTimeLimit,
    };

    return () => {
      ZodiacEngineService.zodiacEngine.aiOrchestrator = null;
      ZodiacEngineService.zodiacEngine.aiTimeLimits = null;
    };
  }, [lightPlayer, darkPlayer, lightModel, darkModel, aiOrchestratorRef, lightTimeLimit, darkTimeLimit, lightBlind, darkBlind]);

  // Auto-trigger AI piece placement when it's their turn
  useEffect(() => {
    if (selectionMode !== 'PieceSelection') return;

    // Ensure game board is initialized before allowing AI placement
    const gameState = ZodiacEngineService.zodiacEngine.getGameState();
    if (!gameState || !gameState.board || gameState.board.length === 0) {
     
      return;
    }

    // Check if all pieces are placed (10 total)
    const lightPieces = Array.from(placedPieces).filter(key => key.endsWith('-light'));
    const darkPieces = Array.from(placedPieces).filter(key => key.endsWith('-dark'));

    if (lightPieces.length === 5 && darkPieces.length === 5) {
     
      checkIfAllPiecesPlaced();
      return;
    }

    const currentPlayerIsAI = isLightPieceTurn
      ? (lightPlayer !== AIPlayerType.PLAYER)
      : (darkPlayer !== AIPlayerType.PLAYER);

    if (currentPlayerIsAI) {
      // Ensure the AI has selected a king before placing pieces
      const aiKing = isLightPieceTurn ? selectedLightKing : selectedDarkKing;
      if (!aiKing) {
      
        return;
      }

      // Check if this AI still has pieces to place
      const placedForCurrentSide = Array.from(placedPieces).filter(key =>
        key.endsWith(isLightPieceTurn ? '-light' : '-dark')
      );

      if (placedForCurrentSide.length < 5) {
       
        const timer = setTimeout(() => {
          placeNextAIPiece();
        }, 800);
        return () => clearTimeout(timer);
      }
    }
  }, [selectionMode, isLightPieceTurn, placedPieces, lightPlayer, darkPlayer, gameState, selectedLightKing, selectedDarkKing]);

  // Handle AI moves
  useEffect(() => {
    if (!isGameStarted || isAIThinking || isPaused) return;
    if (gameState.state !== State.Playing) return;

    // Check if current player is AI
    const currentPlayerIsAI = aiOrchestratorRef.isCurrentPlayerAI(gameState);
  

    if (currentPlayerIsAI) {
      
      setIsAIThinking(true);

      // Timer continues running during AI thinking - AI is on the clock!

      // CRITICAL: Use the current gameState in the closure
      const currentTurnNumber = gameState.turnCount;
      const currentPlayer = gameState.isLightTurn;

      // Request AI move with slight delay for UI responsiveness
      setTimeout(async () => {
        // CRITICAL: Check if time expired before making AI move
        const timeControlState = TimeControlManager.getState();
        if (timeControlState.timeExpired) {
         
          setIsAIThinking(false);
          return;
        }

        // CRITICAL: Verify we're still on the same turn (prevent double-moves)
        const latestState = ZodiacEngineService.zodiacEngine.getGameState();
        if (latestState.turnCount !== currentTurnNumber || latestState.isLightTurn !== currentPlayer) {
        
          setIsAIThinking(false);
          return;
        }

        // CRITICAL: Check if game already ended
        if (latestState.state === State.PostGame) {
        
          setIsAIThinking(false);
          return;
        }
        try {
          // Determine which AI is thinking and use their time limit
          const currentTimeLimit = gameState.isLightTurn ? lightTimeLimit : darkTimeLimit;
          const aiStartTime = Date.now();
          const reviewBefore = hasAstra ? copyState(gameState) : null;
      
          const aiMoveResult = await aiOrchestratorRef.requestAIMove(gameState, currentTimeLimit);
          const aiEndTime = Date.now();
          const aiActualThinkingTime = aiEndTime - aiStartTime;

          // CRITICAL: Deduct AI thinking time from their clock
          // Since JavaScript is single-threaded, the timer couldn't tick while AI was computing
          const aiPlayer = gameState.isLightTurn ? 'light' : 'dark';
          TimeControlManager.deductTime(aiPlayer, aiActualThinkingTime);

          if (aiMoveResult) {
            // Reset failure counter on successful move
            setConsecutiveAIFailures(0);

            // Store move result for evaluation panel
            setLastAIMoveResult(aiMoveResult);

            // Clear piece selection reasonings on first move
            if (pieceSelectionReasonings.length > 0) {
              setPieceSelectionReasonings([]);
            }

            // Extract pieces for makeMove
            const fromSquare = gameState.board[aiMoveResult.fromPosition.row][aiMoveResult.fromPosition.col];
            const toSquare = gameState.board[aiMoveResult.move.position.row][aiMoveResult.move.position.col];
            const piece = fromSquare.Pieces.find(p => p.id === aiMoveResult.move.pieceId);
            const toPiece = aiMoveResult.move.toPiece;

            if (!piece) {
              setIsAIThinking(false);
              return;
            }

            // Make the move in the engine
            const moveSucceeded = await ZodiacEngineService.zodiacEngine.makeMove(
              aiMoveResult.fromPosition,
              aiMoveResult.move.position,
              piece,
              toPiece,
              aiMoveResult.move.moveType,
              aiMoveResult.move.sideEffects
            );

            // Get updated game state
            const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
            if (reviewBefore) {
              astraRecorder.current.record(reviewBefore, newGameState, aiMoveResult,
                reviewBefore.isLightTurn ? reportModel(lightModel, lightBlind) : reportModel(darkModel, darkBlind), currentTimeLimit, moveSucceeded);
              if (newGameState.state !== State.Playing) setAstraReport(astraRecorder.current.finish(newGameState));
            }

   

            // Time control turn change is now handled automatically by ZodiacEngine.changeTurns()

            // Record move if battle is active
            if (aiOrchestratorRef.isBattleActive()) {
              const currentAI = aiOrchestratorRef.getCurrentAI(gameState);
              if (currentAI) {
                aiOrchestratorRef.recordBattleMove(
                  newGameState,
                  aiMoveResult,
                  currentAI.name as AIPlayerType
                );
              }
            }

            // Check if there's a pending showKings toggle request and apply it BEFORE updating game state
            if (showKingsQueued !== null) {
           
              setShowKings(showKingsQueued);
              // Update ref AND game state with the NEW showKings value
              if (challengeConfigRef.current) {
                challengeConfigRef.current.showKings = showKingsQueued;
                (newGameState as any).challengeAIConfig = {
                  ...challengeConfigRef.current,
                };
              }
              setShowKingsQueued(null);
            } else {
              // No queued value, use ref value
              if (challengeConfigRef.current) {
                (newGameState as any).challengeAIConfig = {
                  ...challengeConfigRef.current,
                };
              }
            }

            updateGameState(newGameState);

            // CRITICAL: Check if game ended (draw, win, lose) after the move
            if (newGameState.state !== State.Playing) {
            
              setIsAIThinking(false);
              if (aiOrchestratorRef.isBattleActive()) {
             
                aiOrchestratorRef.stopBattle();
              }
              return; // Stop processing, game is over
            }

            // Check if there's a pending pause request
            if (isPauseQueued) {
           
              setIsPaused(true);
              setIsPauseQueued(false);
            }
          } else {
            // AI failed to find a move - increment failure counter
            setConsecutiveAIFailures(prev => {
              const newCount = prev + 1;

              if (newCount >= MAX_AI_FAILURES) {
                // Force game to end with AI loss
                const aiPlayer = gameState.isLightTurn ? 'light' : 'dark';
                const winner = aiPlayer === 'light' ? 'dark' : 'light';

                // Set game to PostGame
                const currentState = ZodiacEngineService.zodiacEngine.getGameState();
                currentState.state = State.PostGame;
                currentState.lightWon = winner === 'light';
                (currentState as any).winCondition = 'no_moves';

                updateGameState(currentState);
              }

              return newCount;
            });
          }
        } catch (error) {

          // Increment failure counter on exception
          setConsecutiveAIFailures(prev => {
            const newCount = prev + 1;

            if (newCount >= MAX_AI_FAILURES) {
              // Force game to end with AI loss
              const aiPlayer = gameState.isLightTurn ? 'light' : 'dark';
              const winner = aiPlayer === 'light' ? 'dark' : 'light';

              // Set game to PostGame
              const currentState = ZodiacEngineService.zodiacEngine.getGameState();
              currentState.state = State.PostGame;
              currentState.lightWon = winner === 'light';
              (currentState as any).winCondition = 'no_moves';

              updateGameState(currentState);
            }

            return newCount;
          });
        } finally {
          setIsAIThinking(false);
        }
      }, 100);
    }
  }, [gameState, isGameStarted, isAIThinking, isPaused, isPauseQueued, showKingsQueued, aiOrchestratorRef, updateGameState]);

  // Check for game end and record battle
  useEffect(() => {
    if (
      isGameStarted &&
      gameState.state === State.PostGame &&
      !hasShownGameEndPopupRef.current  // Only show once per game (use ref to avoid re-trigger on close)
    ) {
      hasShownGameEndPopupRef.current = true; // Mark as shown

      // Record battle if it was active
      if (aiOrchestratorRef.isBattleActive()) {
        const battleState = aiOrchestratorRef.getBattleState();
        if (battleState) {
          battleReporter.recordGame(battleState);
        }
      }

      // Record piece stats for AI learning
      const isDrawResult = (gameState as any).isDraw || false;
      if (!isDrawResult) {
        // Record stats for light AI (if AI)
        if (lightModel !== null) {
          const lightWon = gameState.lightWon === true;
          PieceStatsCollector.recordGameResult(lightModel, true, gameState, lightWon);
        }
        // Record stats for dark AI (if AI)
        if (darkModel !== null) {
          const darkWon = gameState.lightWon === false;
          PieceStatsCollector.recordGameResult(darkModel, false, gameState, darkWon);
        }
      }

      // Stop the timer
      TimeControlManager.stopOnGameEnd();

      // Determine win condition and result
      const isPlayerLight = lightPlayer === AIPlayerType.PLAYER;
      const playerWon = isPlayerLight ? gameState.lightWon : !gameState.lightWon;

      // Determine win condition
      let condition: WinCondition = 'kingslayer';
      let raceKingType: string | undefined = undefined;
      const winCondition = (gameState as any).winCondition;
      const isDraw = (gameState as any).isDraw || false;

      if (winCondition === 'timeout') {
        condition = 'timeout';
      } else if (winCondition === 'race' || winCondition === 'zombie-race') {
        condition = 'race';
        // Extract the king type that won by race (only set for zombie race)
        raceKingType = (gameState as any).zombieRacePieceType;
      } else if ((gameState as any).threefoldRepetition) {
        condition = 'threefold';
      } else if ((gameState as any).noValidMoves) {
        condition = 'no_moves';
      } else if (isDraw) {
        condition = 'draw';
      }

      // For kingslayer endings, surface which king was captured so the modal can show it
      let capturedKingType: PieceType | undefined;
      let capturedKingIsLight: boolean | undefined;
      if (condition === 'kingslayer' && !isDraw) {
        const playerKing = isPlayerLight ? gameState.lightKing : gameState.darkKing;
        const opponentKing = isPlayerLight ? gameState.darkKing : gameState.lightKing;
        capturedKingType = playerWon ? opponentKing : playerKing;
        capturedKingIsLight = playerWon ? !isPlayerLight : isPlayerLight;
      }

      // Set game end popup state
      const result = isDraw ? 'draw' : (playerWon ? 'won' : 'lost');
      // The ending gets the same instrument as the rest of the UI (see UISoundEffects):
      // a rising phrase for a win, a falling one for a loss, the plain tick for a draw.
      if (result === 'won') playVictorySound();
      else if (result === 'lost') playDefeatSound();
      else playUISound();
      setGameEndResult(result);
      setGameEndCondition(condition);
      setGameEndRaceKingType(raceKingType);
      setGameEndCapturedKingType(capturedKingType);
      setGameEndCapturedKingIsLight(capturedKingIsLight);
      setShowGameEndPopup(true);
    }
  }, [gameState.state, isGameStarted, aiOrchestratorRef, battleReporter, lightPlayer, gameState]);

  // Also finish reports for human moves, clock endings and engine draw states.
  useEffect(() => {
    if (hasAstra && isGameStarted && [State.PostGame, State.Draw, State.Terminated].includes(gameState.state)) {
      setAstraReport(astraRecorder.current.finish(gameState));
    }
  }, [hasAstra, isGameStarted, gameState]);

  // Time expiration handler - subscription + polling backup
  useEffect(() => {
    // Track if we've already handled expiration to prevent double-triggering
    let hasHandledExpiration = false;

    const handleTimeExpiration = () => {
      if (hasHandledExpiration) return;
      if (gameState.state === State.PostGame) return;

      // Get the time state to know who lost on time
      const timeState = TimeControlManager.getState();
      const lightTime = timeState.playerTime?.lightTimeRemaining ?? Infinity;
      const darkTime = timeState.playerTime?.darkTimeRemaining ?? Infinity;

      // Determine winner (opposite of who ran out of time)
      // timeState.winner is already set to the winner by TimeControlManager
      // But also handle case where polling detected 0 time before winner was set
      let lightWon: boolean;
      if (timeState.winner) {
        lightWon = timeState.winner === 'light';
      } else if (lightTime <= 0) {
        lightWon = false; // Light ran out, dark wins
      } else if (darkTime <= 0) {
        lightWon = true; // Dark ran out, light wins
      } else {
        // Fallback: current player loses (they're on the clock)
        lightWon = !gameState.isLightTurn;
      }

      hasHandledExpiration = true;

      // Stop AI thinking immediately
      setIsAIThinking(false);

      // CRITICAL: Directly set game state to PostGame with timeout condition
      // Don't rely on checkGameEnd() which only checks board-based conditions
      const currentState = ZodiacEngineService.zodiacEngine.getGameState();
      currentState.state = State.PostGame;
      currentState.lightWon = lightWon;
      (currentState as any).winCondition = 'timeout';

      // Update React state immediately so GameEndPopup shows
      updateGameState(currentState);

      // Stop the timer
      TimeControlManager.stopOnGameEnd();

      // Stop AI battle if active
      if (aiOrchestratorRef.isBattleActive()) {
        aiOrchestratorRef.stopBattle();
      }
    };

    // Primary: Subscribe to time control events
    const unsubscribe = TimeControlManager.subscribe((event) => {
      if (event.timeExpired && event.winner) {
        handleTimeExpiration();
      }
    });

    // Backup: Poll every 200ms to check if time expired (in case subscription misses it)
    const pollInterval = setInterval(() => {
      if (!isGameStarted || gameState.state === State.PostGame) return;

      const timeState = TimeControlManager.getState();
      if (timeState.timeExpired && timeState.winner) {
        handleTimeExpiration();
      }

      // Untimed games start with 0ms on both clocks — never trigger timeout fallback.
      if (isTimedPreset(timeState.config.preset)) {
        const lightTime = timeState.playerTime?.lightTimeRemaining ?? Infinity;
        const darkTime = timeState.playerTime?.darkTimeRemaining ?? Infinity;

        if (lightTime <= 0 || darkTime <= 0) {
          handleTimeExpiration();
        }
      }
    }, 200);

    return () => {
      unsubscribe();
      clearInterval(pollInterval);
    };
  }, [aiOrchestratorRef, updateGameState, isGameStarted, gameState.state]);

  // Handler for "Start with Selection" button
  const handleStartWithSelection = () => {
 

    // Switch to mobile game page if on mobile
    if (isMobile) {
      setIsMobileGamePage(true);
    }

    // Reset game to empty board for piece placement
    ZodiacEngineService.zodiacEngine.resetToEmptyBoard();
    let newGameState = ZodiacEngineService.zodiacEngine.getGameState();

    // Set game state to KingSelection
    (ZodiacEngineService.zodiacEngine as any).gameStateManager.setGameStateState(State.KingSelection);
    newGameState = ZodiacEngineService.zodiacEngine.getGameState();
    updateGameState(newGameState);

    // Reset selection state
    setSelectedLightKing(null);
    setSelectedDarkKing(null);
    setIsSelectingForLight(true);
    setPlacedPieces(new Set());

    // Set random king placement order (1-5) for both sides
    setLightKingPlacementOrder(Math.floor(Math.random() * 5) + 1);
    setDarkKingPlacementOrder(Math.floor(Math.random() * 5) + 1);

    // Check if light player is human - if so, show king selection, otherwise let AI pick
    if (lightPlayer === AIPlayerType.PLAYER) {
      setSelectionMode('KingSelection');
    } else {
      // Light is AI, let AI pick king
      const eligibleKings = [
        PieceType.Bear, PieceType.Crane, PieceType.Frog, PieceType.Goat, PieceType.Monkey,
        PieceType.Fungi, PieceType.Rhino, PieceType.Snake, PieceType.Tree, PieceType.Wolf
      ];
      const lightAI = aiOrchestratorRef.getAI(lightPlayer);
      const lightKing = lightAI ? lightAI.selectKing(eligibleKings, true) : eligibleKings[Math.floor(Math.random() * eligibleKings.length)];
      setSelectedLightKing(lightKing);
      setIsSelectingForLight(false);

      // Now check dark player
      if (darkPlayer === AIPlayerType.PLAYER) {
        setSelectionMode('KingSelection');
      } else {
        // Both are AI, select both kings and go straight to piece selection
        const darkAI = aiOrchestratorRef.getAI(darkPlayer);
        const darkKing = darkAI ? darkAI.selectKing(eligibleKings, false) : eligibleKings[Math.floor(Math.random() * eligibleKings.length)];
     
        setSelectedDarkKing(darkKing);
      

        // Set game state to PieceSelection
        (ZodiacEngineService.zodiacEngine as any).gameStateManager.setGameStateState(State.PieceSelection);
        const updatedGameState = ZodiacEngineService.zodiacEngine.getGameState();
        updateGameState(updatedGameState);
        setSelectionMode('PieceSelection');

        // If both players are AI, automatically place pieces
        // Pass kings directly to avoid state update timing issues
        setTimeout(() => {
          placeAIPieces(lightKing, darkKing);
        }, 500);
      }
    }
  };

  // Handler for when a king is selected in the modal
  const handleKingSelected = (king: PieceType, isLight: boolean) => {

    if (isLight) {
      setSelectedLightKing(king);
      setIsSelectingForLight(false);

      // Check if dark player is human
      if (darkPlayer === AIPlayerType.PLAYER) {
        // Keep modal open for dark player selection
        // (isSelectingForLight is now false, so modal will show dark selection)
      } else {
        // Dark is AI, let AI pick king
        const eligibleKings = [
          PieceType.Bear, PieceType.Crane, PieceType.Frog, PieceType.Goat, PieceType.Monkey,
          PieceType.Fungi, PieceType.Rhino, PieceType.Snake, PieceType.Tree, PieceType.Wolf
        ];
        const darkAI = aiOrchestratorRef.getAI(darkPlayer);
        const darkKing = darkAI ? darkAI.selectKing(eligibleKings, false) : eligibleKings[Math.floor(Math.random() * eligibleKings.length)];
     
        setSelectedDarkKing(darkKing);
      

        // Set game state to PieceSelection and close modal
        (ZodiacEngineService.zodiacEngine as any).gameStateManager.setGameStateState(State.PieceSelection);
        const updatedGameState = ZodiacEngineService.zodiacEngine.getGameState();
        updateGameState(updatedGameState);
        setSelectionMode('PieceSelection');
      }
    } else {
      // Dark player selected their king
      setSelectedDarkKing(king);

      // Both kings selected, move to piece placement
      (ZodiacEngineService.zodiacEngine as any).gameStateManager.setGameStateState(State.PieceSelection);
      const updatedGameState = ZodiacEngineService.zodiacEngine.getGameState();
      updateGameState(updatedGameState);
      setSelectionMode('PieceSelection');

      // If both players are AI, automatically place pieces
      if (lightPlayer !== AIPlayerType.PLAYER && darkPlayer !== AIPlayerType.PLAYER) {
        setTimeout(() => {
          placeAIPieces();
        }, 500);
      }
    }
  };

  // Place a single AI piece (for alternating with human)
  const placeNextAIPiece = () => {
    const isLight = isLightPieceTurn;
    const aiPlayer = isLight ? lightPlayer : darkPlayer;

    // Check if this player is actually AI
    if (aiPlayer === AIPlayerType.PLAYER) {
    
      return;
    }

  

    // Get already placed pieces for this side
    const placedForThisSide = Array.from(placedPieces).filter(key =>
      key.endsWith(isLight ? '-light' : '-dark')
    );

    // Check if all 5 pieces are placed for this side
    if (placedForThisSide.length >= 5) {
    
      checkIfAllPiecesPlaced();
      return;
    }

    const kingType = isLight ? selectedLightKing : selectedDarkKing;
    const kingPlacementOrder = isLight ? lightKingPlacementOrder : darkKingPlacementOrder;

  

    if (!kingType) {
     
      return;
    }

    // Determine if we should place the king this turn
    let pieceToPlace: PieceType;
    const isKingAlreadyPlaced = placedPieces.has(`${kingType}-${isLight ? 'light' : 'dark'}`);

    // Check if it's time to place the king (based on random order) OR if it's the last piece and king isn't placed
    if (!isKingAlreadyPlaced && (placedForThisSide.length === kingPlacementOrder - 1 || placedForThisSide.length === 4)) {
      pieceToPlace = kingType!;
     
    } else {
      // Get eligible pieces (not already placed, excluding the king if it hasn't been placed yet and it's not time)
      const eligiblePieces = allPieceTypes.filter(type => {
        const pieceKey = `${type}-${isLight ? 'light' : 'dark'}`;
        if (placedPieces.has(pieceKey)) return false;
        if (type === kingType && !isKingAlreadyPlaced) return false; // Save king for its designated slot
        return true;
      });

      // Use strategic piece selection if AI has the method, otherwise random
      const currentAI = aiOrchestratorRef.getAI(aiPlayer);
      if (currentAI && currentAI.selectPiece) {
        // Get opponent's pieces (already placed)
        const opponentPieces: PieceType[] = Array.from(placedPieces)
          .filter(key => key.endsWith(isLight ? '-dark' : '-light'))
          .map(key => key.replace(/-light|-dark/, '') as PieceType);

        // Get own pieces (already placed)
        const ownPieces: PieceType[] = Array.from(placedPieces)
          .filter(key => key.endsWith(isLight ? '-light' : '-dark'))
          .map(key => key.replace(/-light|-dark/, '') as PieceType);

        // Check if AI has the reasoning method (MinimaxAI)
        if ('selectPieceWithReasoning' in currentAI) {
          const reasoning = (currentAI as any).selectPieceWithReasoning(eligiblePieces, opponentPieces, ownPieces);
          pieceToPlace = reasoning.piece;
          setPieceSelectionReasonings(prev => [...prev, reasoning]);
         
        } else {
          pieceToPlace = currentAI.selectPiece(eligiblePieces, opponentPieces, ownPieces);
         
        }
      } else {
        pieceToPlace = eligiblePieces[Math.floor(Math.random() * eligiblePieces.length)];
      
      }
    }

    if (!pieceToPlace) {
      return;
    }

    // Find an empty position
    const row = isLight ? 0 : 7;
    const gameState = ZodiacEngineService.zodiacEngine.getGameState();

    if (!gameState || !gameState.board || !gameState.board[row]) {
      return;
    }

    const availableColumns = [0, 1, 2, 3, 4, 5, 6, 7].filter(col => {
      const square = gameState.board[row][col];
      return square && square.Pieces.length === 0;
    });

    if (availableColumns.length === 0) {
      return;
    }

    const col = availableColumns[Math.floor(Math.random() * availableColumns.length)];
    const position = { row, col };

    // Place the piece
    const piece = new Piece(pieceToPlace, isLight);

    // Set penguin direction
    if (pieceToPlace === 'Penguin') {
      piece.direction = isLight ? 'down' : 'up';
    }

    // Mark as king if this is the selected king
    if (pieceToPlace === kingType) {
      piece.isKing = true;
    }

    ZodiacEngineService.zodiacEngine.placePiece(piece, position);

    const pieceKey = `${pieceToPlace}-${isLight ? 'light' : 'dark'}`;
    setPlacedPieces(prev => new Set(prev).add(pieceKey));

    // Play sound
    const audio = new Audio('/turn change.mp3');
    audio.volume = 0.3;
    audio.play().catch(console.error);

    // Switch turns
    setIsLightPieceTurn(!isLightPieceTurn);

    // Check if game should start
    setTimeout(() => {
      checkIfAllPiecesPlaced();
    }, 500);
  };

  // Check if all 10 pieces are placed and start game
  const checkIfAllPiecesPlaced = () => {
    const lightPieces = Array.from(placedPieces).filter(key => key.endsWith('-light'));
    const darkPieces = Array.from(placedPieces).filter(key => key.endsWith('-dark'));

 

    if (lightPieces.length === 5 && darkPieces.length === 5) {
     
      handleFinishPiecePlacement();
    }
  };

  // AI piece placement: Alternates L-D-L-D-L-D-L-D-L-D (10 pieces total) - for AI vs AI
  const placeAIPieces = (lightKing?: PieceType, darkKing?: PieceType) => {
   
    // Use provided kings or fall back to state (for manual placement mode)
    const actualLightKing = lightKing || selectedLightKing;
    const actualDarkKing = darkKing || selectedDarkKing;

    if (!actualLightKing || !actualDarkKing) {
      return;
    }

    // Defensive check: Ensure board is initialized
    const gameState = ZodiacEngineService.zodiacEngine.getGameState();
    if (!gameState.board || gameState.board.length < 8) {
      return;
    }

    // Track which pieces have been selected (non-king pieces)
    const lightSelectedPieces: PieceType[] = [];
    const darkSelectedPieces: PieceType[] = [];

    // Get AI instances for strategic selection
    const lightAI = aiOrchestratorRef.getAI(lightPlayer);
    const darkAI = aiOrchestratorRef.getAI(darkPlayer);

    // Place pieces for each side in alternating order
    const placements: Array<{ type: PieceType; isLight: boolean; position: { row: number; col: number } }> = [];

    // Track which columns have been reserved for placement to avoid duplicates
    const reservedLightCols = new Set<number>();
    const reservedDarkCols = new Set<number>();

    // First, select 4 non-king pieces per side (8 total, alternating L-D-L-D-L-D-L-D)
    for (let i = 0; i < 8; i++) {
      const isLight = i % 2 === 0; // Even indices = light, odd = dark

      // Determine which pieces are still available (excluding king)
      const selectedKing = isLight ? actualLightKing : actualDarkKing;
      const alreadySelected = isLight ? lightSelectedPieces : darkSelectedPieces;
      const opponentSelected = isLight ? darkSelectedPieces : lightSelectedPieces;

      const eligiblePieces = allPieceTypes.filter(type =>
        type !== selectedKing && !alreadySelected.includes(type)
      );

      if (eligiblePieces.length === 0) {
        continue;
      }

      // Use strategic selection if AI supports it
      const currentAI = isLight ? lightAI : darkAI;
      let piece: PieceType;

      if (currentAI && currentAI.selectPiece) {
        // Check if AI has the reasoning method (MinimaxAI)
        if ('selectPieceWithReasoning' in currentAI) {
          const reasoning = (currentAI as any).selectPieceWithReasoning(eligiblePieces, opponentSelected, alreadySelected);
          piece = reasoning.piece;
          setPieceSelectionReasonings(prev => [...prev, reasoning]);
        
        } else {
          piece = currentAI.selectPiece(eligiblePieces, opponentSelected, alreadySelected);
        
        }
      } else {
        // Fallback to random
        piece = eligiblePieces[Math.floor(Math.random() * eligiblePieces.length)];
   
      }

      // Track selection
      if (isLight) {
        lightSelectedPieces.push(piece);
      } else {
        darkSelectedPieces.push(piece);
      }

      if (!piece) continue;

      // Find an empty position on the appropriate row
      const row = isLight ? 0 : 7;
      const board = gameState.board;

      // Additional defensive check for row existence
      if (!board[row]) {
        continue;
      }

      const reservedCols = isLight ? reservedLightCols : reservedDarkCols;

      // Filter out columns that are already occupied OR reserved for placement
      const availableColumns = [0, 1, 2, 3, 4, 5, 6, 7].filter(col => {
        const square = board[row][col];
        const isEmpty = square && square.Pieces && square.Pieces.length === 0;
        const notReserved = !reservedCols.has(col);
        return isEmpty && notReserved;
      });

      if (availableColumns.length > 0) {
        const col = availableColumns[Math.floor(Math.random() * availableColumns.length)];
        placements.push({ type: piece, isLight, position: { row, col } });
        reservedCols.add(col); // Mark this column as reserved
       
      } else {
      }
    }

    // Now place the kings (Light then Dark)
    for (const isLight of [true, false]) {
      const kingPiece = isLight ? actualLightKing : actualDarkKing;
      const row = isLight ? 0 : 7;
      const board = gameState.board;
      const reservedCols = isLight ? reservedLightCols : reservedDarkCols;

      const availableColumns = [0, 1, 2, 3, 4, 5, 6, 7].filter(col => {
        const square = board[row][col];
        const isEmpty = square && square.Pieces && square.Pieces.length === 0;
        const notReserved = !reservedCols.has(col);
        return isEmpty && notReserved;
      });

      if (availableColumns.length > 0) {
        const col = availableColumns[Math.floor(Math.random() * availableColumns.length)];
        placements.push({ type: kingPiece, isLight, position: { row, col } });
        reservedCols.add(col);
       
      } else {
      }
    }

  

    // Place pieces with delays
    placements.forEach((placement, index) => {
      setTimeout(() => {
        const piece = new Piece(placement.type, placement.isLight);

        // Set penguin direction
        if (placement.type === 'Penguin') {
          piece.direction = placement.isLight ? 'down' : 'up';
        }

        // Mark as king if this is the selected king
        const kingType = placement.isLight ? actualLightKing : actualDarkKing;
        if (placement.type === kingType) {
          piece.isKing = true;
        }

        ZodiacEngineService.zodiacEngine.placePiece(piece, placement.position);

        const pieceKey = `${placement.type}-${placement.isLight ? 'light' : 'dark'}`;
        setPlacedPieces(prev => new Set(prev).add(pieceKey));

        // After all pieces are placed, start the game
        if (index === placements.length - 1) {
          setTimeout(() => {
            handleFinishPiecePlacement(actualLightKing, actualDarkKing);
          }, 500);
        }
      }, index * 200); // 200ms delay between each piece placement
    });
  };

  // Handler for finishing piece placement and starting the game
  const handleFinishPiecePlacement = (lightKing?: PieceType | React.MouseEvent, darkKing?: PieceType) => {
  

    // If first parameter is a MouseEvent, ignore it (button click)
    const isMouseEvent = lightKing && typeof lightKing === 'object' && 'currentTarget' in lightKing;
   
    // Use provided kings or fall back to state
    const actualLightKing = (isMouseEvent || !lightKing) ? selectedLightKing : lightKing as PieceType;
    const actualDarkKing = darkKing || selectedDarkKing;

    if (!actualLightKing || !actualDarkKing) {
      return;
    }

    // Auto-start game for ALL game modes (AI vs AI, Player vs AI, Player vs Player)
    startGameWithPlayers(actualLightKing, actualDarkKing);
  };

  // Core function to start game with specific kings
  const startGameWithPlayers = (lightKing: PieceType, darkKing: PieceType) => {
    astraRecorder.current.start(reportModel(lightModel, lightBlind), reportModel(darkModel, darkBlind), lightTimeLimit, darkTimeLimit);
    setAstraReport(null);
    aiOrchestratorRef.resetAIs();
  

    // CRITICAL FIX: Reset move history BEFORE starting new game
    // This ensures FENHistory records the board WITH pieces as initial position
    // Not the empty board from handleResetGame
    setMoveHistoryResetKey(prev => prev + 1);

    // Initialize game
    const playerIdForLight = user?.uid || 'challenge-ai-player';

    // ALWAYS use TestAI mode for ChallengeAI games (even AI-vs-AI)
    // This prevents GameContext from triggering the old zodiacEngine.makeAIMove()
    // ChallengeAI's own useEffect (lines 128-258) handles AI moves via AIOrchestrator
   

    // CRITICAL FIX: Only capture board if coming from piece selection (manually-placed pieces)
    // If coming from "Start Random", pass null to let switchToTestAIMode spawn random pieces
    const currentBoard = selectionMode === 'PieceSelection'
      ? ZodiacEngineService.zodiacEngine.getGameState().board
      : null;

    ZodiacEngineService.zodiacEngine.switchToTestAIMode(
      playerIdForLight,
      lightKing,
      darkKing,
      currentBoard // Pass existing board only if manually placed, otherwise null for random
    );

    // CRITICAL FIX: Explicitly notify ONCE after board is fully set up
    // This ensures FENHistory records the final board state, not intermediate states
    (ZodiacEngineService.zodiacEngine as any).eventManager.notifyLocalListeners();

    const newGameState = ZodiacEngineService.zodiacEngine.getGameState();

    // Store challenge config in ref so it persists across game state updates
    challengeConfigRef.current = {
      lightPlayer,
      darkPlayer,
      showKings: false, // Default to hidden for fog of war
    };

    // Add Challenge AI specific config to game state
    (newGameState as any).challengeAIConfig = {
      ...challengeConfigRef.current,
    };

   
    updateGameState(newGameState);

    // Start battle if both are AIs
    if (lightPlayer !== AIPlayerType.PLAYER && darkPlayer !== AIPlayerType.PLAYER) {
      battleReporter.startBattle(lightPlayer, darkPlayer);
      aiOrchestratorRef.startBattle(
        {
          lightPlayer,
          darkPlayer,
          minMoveDelay: 1000,
          maxThinkingTime: 10000,
          enableAnalysis: true,
        },
        newGameState
      );
     
    }

    // Initialize time control. Welcome flow forces OFF for the first match;
    // every other game uses the saved preference.
    const effectivePreset = forceUntimedRef.current ? TimeControlPreset.OFF : timeControlPreset;
    forceUntimedRef.current = false;
    setActivePreset(effectivePreset);
    const timeConfig = getPresetConfig(effectivePreset);

    TimeControlManager.initialize(timeConfig, 'local');

    // Start timer for Light player (game always starts with Light).
    // No-op when preset is OFF — TimeControlManager.startTimer short-circuits.
    TimeControlManager.startTimer('light');

    setIsGameStarted(true);
    setSelectionMode('None'); // Clear selection mode
  };

  // Handler for "Start Random" - original behavior
  const handleStartGame = () => {
   
    // Switch to mobile game page if on mobile
    if (isMobile) {
      setIsMobileGamePage(true);
    }

    // Get eligible king types
    const eligibleKings = [
      PieceType.Bear, PieceType.Crane, PieceType.Frog, PieceType.Goat, PieceType.Monkey,
      PieceType.Fungi, PieceType.Rhino, PieceType.Snake, PieceType.Tree, PieceType.Wolf
    ];

    // Select kings using AI selection or random for human players
    let lightKing: PieceType;
    let darkKing: PieceType;

    if (lightPlayer === AIPlayerType.PLAYER) {
      // Human player - random selection for now
      lightKing = eligibleKings[Math.floor(Math.random() * eligibleKings.length)];
    
    } else {
      // AI player - use AI's selectKing() method
      const lightAI = aiOrchestratorRef.getAI(lightPlayer);
      if (lightAI) {
        lightKing = lightAI.selectKing(eligibleKings, true);
   
      } else {
        lightKing = eligibleKings[Math.floor(Math.random() * eligibleKings.length)];
      }
    }

    if (darkPlayer === AIPlayerType.PLAYER) {
      // Human player - random selection for now
      darkKing = eligibleKings[Math.floor(Math.random() * eligibleKings.length)];
     
    } else {
      // AI player - use AI's selectKing() method
      const darkAI = aiOrchestratorRef.getAI(darkPlayer);
      if (darkAI) {
        darkKing = darkAI.selectKing(eligibleKings, false);
       
      } else {
        darkKing = eligibleKings[Math.floor(Math.random() * eligibleKings.length)];
      }
    }

    startGameWithPlayers(lightKing, darkKing);
  };

  const handleLoadTestPosition = () => {
   

    // Test FEN: F6f/BK6bk/C6c/8/8 - Frogs on row 0, Bears on row 1, Cranes on row 2
    const testFEN = 'F6f/BK6bk/C6c/8/8';

    // IMPORTANT: Initialize game mode FIRST (this will place random pieces, but we'll overwrite them)
    const playerIdForLight = user?.uid || 'challenge-ai-player';
    ZodiacEngineService.zodiacEngine.switchToTestAIMode(playerIdForLight, PieceType.Frog, PieceType.Frog);

    // Now clear the board and load our specific FEN position
    ZodiacEngineService.zodiacEngine.getGameState().board.forEach(row => {
      row.forEach(square => {
        square.Pieces = [];
      });
    });

    // Load the FEN position
    const result = ZodiacEngineService.zodiacEngine.loadFromFEN(testFEN);

    if (!result.success) {
      return;
    }

    const newGameState = ZodiacEngineService.zodiacEngine.getGameState();

    // Set up challenge config
    challengeConfigRef.current = {
      lightPlayer,
      darkPlayer,
      showKings: false,
    };

    (newGameState as any).challengeAIConfig = {
      ...challengeConfigRef.current,
    };

  
    setIsGameStarted(true);
    setMoveHistoryResetKey(prev => prev + 1);
  };

  const handlePauseToggle = () => {
    if (isPaused) {
      // Resuming - apply immediately and clear any pending pause
      setIsPaused(false);
      setIsPauseQueued(false);
      TimeControlManager.resumeTimer();
     
    } else {
      // Pausing - ALWAYS set isPaused for instant visual feedback
      setIsPaused(true);
      TimeControlManager.pauseTimer();
      if (isAIThinking) {
        // AI is thinking - also queue to ensure it applies after current move
        setIsPauseQueued(true);
       
      } else {
        // AI not thinking - pause is already applied above
      
      }
    }
  };

  const handleResetGame = () => {
    // Clear pause states
    setIsPaused(false);
    setIsPauseQueued(false);
    setShowKings(false); // Reset king visibility to hidden
    setShowKingsQueued(null); // Clear any queued showKings toggle
    challengeConfigRef.current = null; // Clear challenge config

    // Clear game end popup
    setShowGameEndPopup(false);
    setGameEndCapturedKingType(undefined);
    setGameEndCapturedKingIsLight(undefined);
    hasShownGameEndPopupRef.current = false; // Reset for new game

    // Clear selection state
    setSelectionMode('None');
    setSelectedLightKing(null);
    setSelectedDarkKing(null);
    setIsSelectingForLight(true);
    setPlacedPieces(new Set());
    setIsLightPieceTurn(true); // Reset to light's turn

    if (aiOrchestratorRef.isBattleActive()) {
      const battle = battleReporter.finishBattle();
      if (battle) {
    
      }
      aiOrchestratorRef.stopBattle();
    }

    // Cleanup time control. Drop any forced-OFF override so the next game
    // honours the saved preference again.
    TimeControlManager.cleanup();
    setActivePreset(timeControlPreset);

    setIsGameStarted(false);
    setIsAIThinking(false);
    setIsPaused(false);

    // CRITICAL: Don't increment moveHistoryResetKey here!
    // It should be incremented when starting the NEW game, not during reset
    // This prevents recording empty board as initial position

    // Reset to an empty board (8/8/8/8/8) - user expects clean slate
    ZodiacEngineService.zodiacEngine.resetToEmptyBoard();
    const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
    updateGameState(newGameState);
  };

  // Reset + immediately start a new random game with the current settings.
  // Uses pendingRematch so handleStartGame runs after handleResetGame's state
  // updates have committed.
  // THE ONE WAY A GAME BEGINS. Start, the rematch button and PLAY NOW all come through
  // here, so "which opening" is answered in a single place by the toggle rather than by
  // which button the player happened to press.
  const startGameByPreference = () => {
    if (randomStart) handleStartGame();
    else handleStartWithSelection();
  };

  const handleRematch = () => {
    if (seriesTimer.current) clearTimeout(seriesTimer.current);
    handleResetGame();
    setPendingRematch(true);
  };

  useEffect(() => {
    if (!pendingRematch) return;
    if (isGameStarted) return;
    setPendingRematch(false);
    startGameByPreference();  // rematch deals the same kind of game the toggle is set to
  }, [pendingRematch, isGameStarted]);

  useEffect(() => {
    if (!seriesActive || !seriesReady || seriesStarted.current) return;
    seriesStarted.current = true;
    startGameByPreference();
  }, [seriesActive, seriesReady]);

  useEffect(() => {
    if (!seriesActive || !seriesReady || !isGameStarted || !astraReport?.finishedAt || seriesRecorded.current.has(astraReport.id)) return;
    if (new Date(astraReport.startedAt).getTime() < new Date(series.startedAt).getTime()) return;
    seriesRecorded.current.add(astraReport.id);
    const next = { ...series, games: [...series.games, astraReport] };
    saveSeries(next).then(() => {
      setSeries(next);
      if (!seriesActiveRef.current) return;
      setSeriesMessage('Game saved. Next king selection starts in 8 seconds.');
      seriesTimer.current = setTimeout(() => {
        if (!seriesActiveRef.current) return;
        setSeriesMessage('Recording | You: Light | ZSTR Blind: Dark | Deep 20s');
        handleRematch();
      }, 8000);
    }).catch(() => {
      setSeries(next); setSeriesActive(false);
      setSeriesMessage('Storage failed. Download games to keep this result; automatic rematches stopped.');
    });
  }, [astraReport?.id, seriesActive, seriesReady, isGameStarted]);

  const endSeries = () => {
    if (seriesTimer.current) clearTimeout(seriesTimer.current);
    seriesActiveRef.current = false;
    setSeriesActive(false); setSeriesMessage('Session ended. Completed games are saved.');
    downloadSeries(series);
  };
  const renderSeries = () => seriesRequested && (
    <section className={styles.astraReview} aria-label="Blind ZSTR playtest session">
      <h3>Blind ZSTR playtest</h3>
      <p>{seriesMessage}</p>
      <p>You {series.games.filter(g => g.outcome === 'light wins').length} / ZSTR {series.games.filter(g => g.outcome === 'dark wins').length} / Draws {series.games.filter(g => g.outcome === 'draw').length}</p>
      <button type="button" onClick={() => downloadSeries(series)}>Download session games</button>
      {seriesActive && <button type="button" onClick={endSeries}>End session</button>}
    </section>
  );

  const handleDrop = (event: React.DragEvent<HTMLDivElement>, position: any) => {
    // Only handle drops in piece placement mode
    if (selectionMode !== 'PieceSelection') return;

    event.preventDefault();
    const pieceData = event.dataTransfer.getData("piece");

    if (!pieceData) return;

    const parsedData = JSON.parse(pieceData);
    const { type, isLight } = parsedData;

 

    // Check if it's this player's turn to place
    if (isLight !== isLightPieceTurn) {
    
      setWarningMessage(isLightPieceTurn ? t('challengeAI.warningLightTurn') : t('challengeAI.warningDarkTurn'));
      return;
    }

    // Validate placement row: Light pieces MUST go on row 0, Dark pieces MUST go on row 7
    const requiredRow = isLight ? 0 : 7;
    if (position.row !== requiredRow) {
     
      setWarningMessage(isLight ? t('challengeAI.warningLightRow') : t('challengeAI.warningDarkRow'));
      return;
    }

    // Check if piece is already on the board
    const pieceKey = `${type}-${isLight ? 'light' : 'dark'}`;
    if (placedPieces.has(pieceKey)) {
     
      return;
    }

    // Count how many pieces of this color are already placed
    const placedCount = Array.from(placedPieces).filter(key =>
      key.endsWith(isLight ? '-light' : '-dark')
    ).length;

    // Check if this is the 5th piece and king hasn't been placed yet
    const kingType = isLight ? selectedLightKing : selectedDarkKing;
    const kingKey = `${kingType}-${isLight ? 'light' : 'dark'}`;
    const kingPlaced = placedPieces.has(kingKey);
    const isLocalPvP = lightPlayer === AIPlayerType.PLAYER && darkPlayer === AIPlayerType.PLAYER;

    if (placedCount === 4 && !kingPlaced && type !== kingType) {
      // In local PvP, silently reject to avoid revealing king identity
      // In other modes, show warning modal
      if (!isLocalPvP) {
        setWarningMessage(t('challengeAI.warningKingRequired').replace('{kingType}', kingType || ''));
      }
      return;
    }

    try {
      const piece = new Piece(type as PieceType, isLight);

      // Set penguin direction
      if (type === 'Penguin') {
        piece.direction = isLight ? 'down' : 'up';
      }

      // Mark as king if this is the selected king piece
      if (type === kingType) {
        piece.isKing = true;
      }

      ZodiacEngineService.zodiacEngine.placePiece(piece, position);

      // Mark piece as placed
      setPlacedPieces(prev => new Set(prev).add(pieceKey));

      // Play sound
      const audio = new Audio('/turn change.mp3');
      audio.volume = 0.3;
      audio.play().catch(console.error);

      // Switch turns (useEffect will auto-trigger AI placement)
      setIsLightPieceTurn(!isLightPieceTurn);

    } catch (error) {
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handlePieceDragStart = (event: React.DragEvent<HTMLImageElement>, pieceType: PieceType, isLight: boolean) => {
    event.dataTransfer.setData(
      "piece",
      JSON.stringify({ type: pieceType, isLight })
    );
  };

  // Render time frame selection
  const renderTimeFrameSelection = (
    selectedTimeFrame: TimeFrame,
    setTimeFrame: (timeFrame: TimeFrame) => void,
    disabled: boolean
  ) => {
    return (
      <div className={`${styles.timeFrameSelection} ${styles.aiOptionsGrid}`}>
        {Object.values(TimeFrame).map((timeFrame) => {
          const config = TIME_FRAME_CONFIG[timeFrame];
          const isSelected = selectedTimeFrame === timeFrame;

          return (
            <div
              key={timeFrame}
              className={`${styles.timeFrameButton} ${isSelected ? styles.timeFrameButtonSelected : ''} ${
                disabled ? styles.aiGridCellDisabled : ''
              }`}
              onClick={() => !disabled && setTimeFrame(timeFrame)}
              title={config.description}
            >
              {config.displayName}
            </div>
          );
        })}
      </div>
    );
  };

  // Render time control selection (game clock)
  const renderTimeControlSelection = (
    selectedPreset: TimeControlPreset,
    setPreset: (preset: TimeControlPreset) => void,
    disabled: boolean
  ) => {
    const presets = [TimeControlPreset.OFF, TimeControlPreset.SPRINT, TimeControlPreset.STANDARD, TimeControlPreset.MARATHON];
    const selectedDescription = getPresetDescription(selectedPreset);

    return (
      <>
        <div className={`${styles.timeFrameSelection} ${styles.timeControlGrid}`}>
          {presets.map((preset) => {
            const isSelected = selectedPreset === preset;
            const displayName = getPresetDisplayName(preset);
            const description = getPresetDescription(preset);

            return (
              <div
                key={preset}
                className={`${styles.timeFrameButton} ${isSelected ? styles.timeFrameButtonSelected : ''} ${
                  disabled ? styles.aiGridCellDisabled : ''
                }`}
                onClick={() => !disabled && setPreset(preset)}
                title={description}
              >
                {displayName}
              </div>
            );
          })}
        </div>
        {/* Show description of selected time control */}
        <div style={{
          textAlign: 'center',
          fontSize: '0.75rem',
          color: '#a0a0a0',
          marginTop: '6px',
          fontStyle: 'italic'
        }}>
          {selectedDescription}
        </div>
      </>
    );
  };

  // Render Human / AI selection. The specific AI model is determined by the
  // separate Difficulty selector below.
  const renderModelSelection = (
    selectedModel: AIModel | null,
    setModel: (model: AIModel | null) => void,
    difficulty: Difficulty,
    disabled: boolean,
  ) => {
    return (
      <div className={styles.playerSelectionButtons}>
        {/* Human Player button */}
        <div
          className={`${styles.playerOptionButton} ${selectedModel === null ? styles.playerOptionSelected : ''} ${
            disabled ? styles.aiGridCellDisabled : ''
          }`}
          onClick={() => !disabled && setModel(null)}
        >
          <div className={styles.playerOptionText}>{t('challengeAI.humanPlayer')}</div>
        </div>

        {/* AI button - model derived from current difficulty */}
        <div
          className={`${styles.playerOptionButton} ${styles.aiModelButton} ${selectedModel !== null ? styles.playerOptionSelected : ''} ${
            disabled ? styles.aiGridCellDisabled : ''
          }`}
          onClick={() => !disabled && selectedModel === null && setModel(DIFFICULTY_TO_MODEL[difficulty])}
        >
          <div className={styles.playerOptionText}>{t('challengeAI.aiPlayer')}</div>
        </div>
      </div>
    );
  };

  // Model choices are independent of thinking time.
  const renderDifficultySelection = (
    selectedDifficulty: Difficulty,
    setDifficulty: (d: Difficulty) => void,
    setModel: (m: AIModel | null) => void,
    disabled: boolean,
  ) => {
    return (
      <div className={`${styles.timeFrameSelection} ${styles.aiOptionsGrid} ${styles.modelOptionsGrid}`}>
        {Object.values(Difficulty).filter(d => d !== Difficulty.ZSTR_BLIND).map((difficulty) => {
          const isSelected = selectedDifficulty === difficulty;
          return (
            <div
              key={difficulty}
              className={`${styles.timeFrameButton} ${isSelected ? styles.timeFrameButtonSelected : ''} ${
                disabled ? styles.aiGridCellDisabled : ''
              }`}
              onClick={() => {
                if (disabled) return;
                setDifficulty(difficulty);
                setModel(DIFFICULTY_TO_MODEL[difficulty]);
              }}
            >
              {getDifficultyLabel(difficulty)}
            </div>
          );
        })}
      </div>
    );
  };

  // Render piece selection UI (for Human vs AI modes - Local 2P handled separately in JSX)
  const renderPieceSelection = () => {
    const showLightPieces = lightPlayer === AIPlayerType.PLAYER;
    const showDarkPieces = darkPlayer === AIPlayerType.PLAYER;

    // Hide entire panel once the human player(s) have placed all their pieces
    const lightPlacedCount = Array.from(placedPieces).filter(k => k.endsWith('-light')).length;
    const darkPlacedCount = Array.from(placedPieces).filter(k => k.endsWith('-dark')).length;
    const lightDone = !showLightPieces || lightPlacedCount >= 5;
    const darkDone = !showDarkPieces || darkPlacedCount >= 5;
    if (lightDone && darkDone) return null;

    return (
      <div className={sharedStyles.piecePaletteContainer}>
        <h3 className={sharedStyles.piecePaletteTitle}>{t('challengeAI.dragPieces')}</h3>
        {showLightPieces && (
          <PieceSelectionPanel
            placedPieces={placedPieces}
            selectedKing={selectedLightKing}
            isLight={true}
            onPieceDragStart={handlePieceDragStart}
            hideKingIndicator={false}
          />
        )}
        {showDarkPieces && (
          <PieceSelectionPanel
            placedPieces={placedPieces}
            selectedKing={selectedDarkKing}
            isLight={false}
            onPieceDragStart={handlePieceDragStart}
            hideKingIndicator={false}
          />
        )}
      </div>
    );
  };

  // Mobile game page - just board with buttons
  if (isMobile && isMobileGamePage) {
    return (
      <AdSenseLayout showAds={false}>
        <div className={`${styles.container} ${theme === 'dark' ? styles.dark : ''} ${globalStyles.app} ${styles.mobileGamePage}`} data-board-size="game-mobile">
          <WispyStars />
          <GlobalHeader />

        <TurnPopover
          isThisPlayersTurn={gameState.isLightTurn === true}
          state={gameState.state}
          mode={gameState.mode}
        />

        {/* AI Thinking Modal */}
        <AIThinking
          isVisible={isAIThinking}
          mode={gameState.mode}
          state={gameState.state}
          centered={true}
        />

        <div className={styles.mobileGameContainer} style={{ position: 'relative' }}>

          {/* Dark Player Timer (above board). Slot is always rendered with
              a fixed min-height so the board's top edge stays clear of the
              GlobalHeader chrome even before the game starts (piece-selection
              phase, where the timer itself is hidden). */}
          <div style={{ display: 'flex', width: '100%', paddingLeft: '10px', minHeight: '32px' }}>
            {(isGameStarted || gameState.state === State.Playing) && (
              <TimerDisplay player="dark" isActive={gameState.isLightTurn === false} />
            )}
          </div>

          <div className={styles.boardContainer}>
            <Board
              gameState={gameState}
              uiState={{
                potentialMoves: potentialMoves,
                potentialSideEffects: potentialSideEffects,
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onSquareClick={(() => {
                const shouldProvideHandler = selectionMode === 'PieceSelection' && selectedPieceForPlacement;
               
                return shouldProvideHandler ? handleMobileSquareClick : undefined;
              })()}
              highlightedRows={selectionMode === 'PieceSelection' && (lightPlayer === AIPlayerType.PLAYER || darkPlayer === AIPlayerType.PLAYER) ? {
                light: lightPlayer === AIPlayerType.PLAYER && isLightPieceTurn ? 0 : null,
                dark: darkPlayer === AIPlayerType.PLAYER && !isLightPieceTurn ? 7 : null
              } : undefined}
            />

            {/* King Selection Overlay for Mobile */}
            {selectionMode === 'KingSelection' && (
              <div className={styles.mobileOverlay}>
                <div className={styles.mobileOverlayContent}>
                  <h3>{t('challengeAI.selectYourKing')}</h3>
                  <div className={styles.mobileKingGrid}>
                    {allPieceTypes
                      .filter((pieceType) => pieceType !== PieceType.Virus)
                      .map((pieceType) => (
                      <div
                        key={pieceType}
                        className={styles.mobileKingOption}
                        onClick={() => handleKingSelected(pieceType, isSelectingForLight)}
                      >
                        <img
                          src={`/Icons/${pieceType}-${isSelectingForLight ? 'light' : 'dark'}.png`}
                          alt={pieceType}
                          className={styles.mobileKingIcon}
                        />
                        <span>{pieceType}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Piece Placement Selector for Mobile */}
            {selectionMode === 'PieceSelection' && (lightPlayer === AIPlayerType.PLAYER || darkPlayer === AIPlayerType.PLAYER) && (() => {
              const isLight = lightPlayer === AIPlayerType.PLAYER && isLightPieceTurn;
              const isLocalPvP = lightPlayer === AIPlayerType.PLAYER && darkPlayer === AIPlayerType.PLAYER;
              const sideSuffix = isLight ? 'light' : 'dark';
              const placedCountForSide = Array.from(placedPieces).filter(k => k.endsWith(`-${sideSuffix}`)).length;
              const kingType = isLight ? selectedLightKing : selectedDarkKing;
              const kingKey = kingType ? `${kingType}-${sideSuffix}` : null;
              const kingIsPlaced = kingKey ? placedPieces.has(kingKey) : false;
              const mustPlaceKing = !isLocalPvP && kingType !== null && placedCountForSide === 4 && !kingIsPlaced;

              return (
                <div className={styles.mobilePieceSelector}>
                  <h4>{t('challengeAI.selectPieceToPlace')}</h4>
                  <div className={styles.mobilePieceGrid}>
                    {allPieceTypes
                      .filter((pieceType) => {
                        const showLight = lightPlayer === AIPlayerType.PLAYER && isLightPieceTurn;
                        const showDark = darkPlayer === AIPlayerType.PLAYER && !isLightPieceTurn;
                        if (!showLight && !showDark) return false;
                        const pieceKey = `${pieceType}-${sideSuffix}`;
                        return !placedPieces.has(pieceKey);
                      })
                      .map((pieceType) => {
                        const isKing = pieceType === kingType;
                        const isSelected = selectedPieceForPlacement?.type === pieceType;
                        const isDisabled = mustPlaceKing && !isKing;

                        return (
                          <div
                            key={pieceType}
                            className={`${styles.mobilePieceOption} ${isSelected ? styles.mobilePieceSelected : ''} ${isDisabled ? styles.mobilePieceDisabled : ''}`}
                            onClick={() => {
                              if (isDisabled) return;
                              setSelectedPieceForPlacement({ type: pieceType, isLight });
                            }}
                          >
                            {isKing && !isLocalPvP && <div className={styles.mobileKingBadge}>👑</div>}
                            <img
                              src={`/Icons/${pieceType}-${isLight ? 'light' : 'dark'}.png`}
                              alt={pieceType}
                              className={styles.mobilePieceIcon}
                            />
                            <span className={styles.mobilePieceName}>{pieceType}</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              );
            })()}

            {/* Move History Modal for Mobile */}
            {showMobileHistory && (
              <div className={styles.mobileHistoryOverlay} onClick={() => setShowMobileHistory(false)}>
                <div className={styles.mobileHistoryModal} onClick={(e) => e.stopPropagation()}>
                  <div className={styles.mobileHistoryHeader}>
                    <h3>{t('challengeAI.moveHistory')}</h3>
                    <button className={styles.mobileHistoryClose} onClick={() => setShowMobileHistory(false)}>
                      ✕
                    </button>
                  </div>
                  <div className={styles.mobileHistoryContent}>
                    <FENHistory ref={fenHistoryRef} moveHistoryResetKey={moveHistoryResetKey} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Light Player Timer (below board). Same fixed-slot trick so the
              vertical layout doesn't shift between piece-selection and play. */}
          <div style={{ display: 'flex', width: '100%', paddingLeft: '10px', minHeight: '32px' }}>
            {(isGameStarted || gameState.state === State.Playing) && (
              <TimerDisplay player="light" isActive={gameState.isLightTurn === true} />
            )}
          </div>

          {/* Mobile Game Info - simple text only - Show only during playing phase */}
          {isGameStarted && (
            <div className={styles.mobileGameInfo}>
              <GameInfo />
            </div>
          )}

          <div className={styles.mobileButtons}>
            {renderSeries()}
            {isGameStarted && (
              <>
                <button className={styles.mobileTurnIndicator}>
                  {gameState.isLightTurn ? t('challengeAI.lightTurn') : t('challengeAI.darkTurn')}
                </button>
                <button className={styles.mobileHistoryButton} onClick={() => setShowMobileHistory(true)}>
                  {t('challengeAI.historyButton')}
                </button>
              </>
            )}
            <button className={styles.mobileBackButton} onClick={() => { playUISound(); handleMobileBack(); }}>
              {t('challengeAI.backButton')}
            </button>
          </div>
        </div>

        {/* Game End Popup for Mobile */}
        <GameEndPopup
          isVisible={showGameEndPopup}
          result={gameEndResult}
          condition={gameEndCondition}
          raceKingType={gameEndRaceKingType}
          capturedKingType={gameEndCapturedKingType}
          capturedKingIsLight={gameEndCapturedKingIsLight}
          onClose={() => setShowGameEndPopup(false)}
          onRematch={handleRematch}
          onBackToMenu={() => {
            setIsMobileGamePage(false);
            handleResetGame();
          }}
        />
        </div>
      </AdSenseLayout>
    );
  }

  return (
    <AdSenseLayout>
      <div className={`${styles.container} ${theme === 'dark' ? styles.dark : ''} ${globalStyles.app}`}>
        <WispyStars />
        <GlobalHeader />

        {/* Home button - top left */}
        <button
          className={styles.homeBackButton}
          onClick={() => { playUISound(); handleHome(); }}
          aria-label={t('challengeAI.homeButton')}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>

        <TurnPopover
          isThisPlayersTurn={gameState.isLightTurn === true}
          state={gameState.state}
          mode={gameState.mode}
        />

        {/* AI Thinking Modal */}
        <AIThinking
          isVisible={isAIThinking}
          mode={gameState.mode}
          state={gameState.state}
          centered={true}
        />

      {/* Left Panel */}
      <div className={styles.leftPanel}>
        {renderSeries()}
        <h1 className={styles.title}>{t('challengeAI.title')}</h1>

        <div className={styles.description}>
          <p>{t('challengeAI.tagline')}</p>
        </div>

        {/* Light Player Selection */}
        <div className={styles.playerSelectionBox}>
          <h2 className={styles.sectionTitle}>{t('challengeAI.lightPlayer')}</h2>
          {renderModelSelection(lightModel, setLightModel, lightDifficulty, isGameStarted)}
          {lightModel !== null && (
            <>
              <h3 className={styles.sectionTitle} style={{ fontSize: '0.8rem', marginTop: '4px', marginBottom: '3px' }}>
                {t('challengeAI.difficulty')}
              </h3>
              {renderDifficultySelection(lightDifficulty, setLightDifficulty, setLightModel, isGameStarted)}
              <h3 className={styles.sectionTitle} style={{ fontSize: '0.8rem', marginTop: '4px', marginBottom: '3px' }}>
                {t('challengeAI.thinkingTime')}
              </h3>
              {renderTimeFrameSelection(lightTimeFrame, setLightTimeFrame, isGameStarted)}
              <h3 className={styles.blindTitle}>King blindness</h3>
              <div className={`${styles.timeFrameSelection} ${styles.blindSelection}`} role="group" aria-label="Light AI king blindness">
                {[false, true].map(blind => (
                  <button key={String(blind)} type="button" aria-pressed={lightBlind === blind}
                    className={`${styles.timeFrameButton} ${styles.blindOption} ${lightBlind === blind ? styles.timeFrameButtonSelected : ''} ${isGameStarted ? styles.aiGridCellDisabled : ''}`}
                    disabled={isGameStarted} onClick={() => setLightBlind(blind)}>
                    {blind ? 'On' : 'Off'}
                  </button>
                ))}
              </div>
              <p className={styles.blindHint}>{lightBlind ? 'Opponent king hidden' : 'Both kings visible'}</p>
            </>
          )}
        </div>

        {/* Dark Player Selection */}
        <div className={styles.playerSelectionBox}>
          <h2 className={styles.sectionTitle}>{t('challengeAI.darkPlayer')}</h2>
          {renderModelSelection(darkModel, setDarkModel, darkDifficulty, isGameStarted)}
          {darkModel !== null && (
            <>
              <h3 className={styles.sectionTitle} style={{ fontSize: '0.8rem', marginTop: '4px', marginBottom: '3px' }}>
                {t('challengeAI.difficulty')}
              </h3>
              {renderDifficultySelection(darkDifficulty, setDarkDifficulty, setDarkModel, isGameStarted)}
              <h3 className={styles.sectionTitle} style={{ fontSize: '0.8rem', marginTop: '4px', marginBottom: '3px' }}>
                {t('challengeAI.thinkingTime')}
              </h3>
              {renderTimeFrameSelection(darkTimeFrame, setDarkTimeFrame, isGameStarted)}
              <h3 className={styles.blindTitle}>King blindness</h3>
              <div className={`${styles.timeFrameSelection} ${styles.blindSelection}`} role="group" aria-label="Dark AI king blindness">
                {[false, true].map(blind => (
                  <button key={String(blind)} type="button" aria-pressed={darkBlind === blind}
                    className={`${styles.timeFrameButton} ${styles.blindOption} ${darkBlind === blind ? styles.timeFrameButtonSelected : ''} ${isGameStarted ? styles.aiGridCellDisabled : ''}`}
                    disabled={isGameStarted} onClick={() => setDarkBlind(blind)}>
                    {blind ? 'On' : 'Off'}
                  </button>
                ))}
              </div>
              <p className={styles.blindHint}>{darkBlind ? 'Opponent king hidden' : 'Both kings visible'}</p>
            </>
          )}
        </div>

        {/* Time Control Selection (applies to both players) */}
        <div className={styles.playerSelectionBox}>
          <h2 className={styles.sectionTitle}>Time Control</h2>
          {renderTimeControlSelection(timeControlPreset, setTimeControlPreset, isGameStarted)}
        </div>

        {/* Random start — decides what Start (and PLAY NOW) deals. Uses the existing
            "Start Random" string as its heading so no new translation key is needed for
            a concept every language already has a word for on this screen. */}
        <div className={styles.playerSelectionBox}>
          <h2 className={styles.sectionTitle}>{t('challengeAI.startRandomButton')}</h2>
          <div className={styles.timeFrameSelection}>
            <div
              className={`${styles.timeFrameButton} ${!randomStart ? styles.timeFrameButtonSelected : ''} ${
                isGameStarted ? styles.aiGridCellDisabled : ''
              }`}
              onClick={() => !isGameStarted && setRandomStart(false)}
              title="Start with king and piece selection"
            >
              Off
            </div>
            <div
              className={`${styles.timeFrameButton} ${randomStart ? styles.timeFrameButtonSelected : ''} ${
                isGameStarted ? styles.aiGridCellDisabled : ''
              }`}
              onClick={() => !isGameStarted && setRandomStart(true)}
              title="Start with a random army and king, no selection"
            >
              On
            </div>
          </div>
        </div>

        <div className={styles.gameInfo}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('challengeAI.matchupLabel')}</span>
            <span className={styles.infoValue}>
              {lightPlayer === AIPlayerType.PLAYER && darkPlayer === AIPlayerType.PLAYER
                ? t('challengeAI.matchupLocal2P')
                : lightPlayer !== AIPlayerType.PLAYER && darkPlayer !== AIPlayerType.PLAYER
                ? `${getDifficultyLabel(lightDifficulty)}${lightBlind ? ' Blind' : ''} ${t('challengeAI.aiPlayer')} vs ${getDifficultyLabel(darkDifficulty)}${darkBlind ? ' Blind' : ''} ${t('challengeAI.aiPlayer')}`
                : lightPlayer === AIPlayerType.PLAYER
                ? `${t('challengeAI.matchupPlayerVs')} ${getDifficultyLabel(darkDifficulty)}${darkBlind ? ' Blind' : ''} ${t('challengeAI.aiPlayer')}`
                : `${getDifficultyLabel(lightDifficulty)}${lightBlind ? ' Blind' : ''} ${t('challengeAI.aiPlayer')} ${t('challengeAI.matchupVsPlayer')}`}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('challengeAI.statusLabel')}</span>
            <span className={styles.infoValue}>
              {isPaused
                ? t('challengeAI.statusPaused')
                : isAIThinking
                ? t('challengeAI.statusAIThinking')
                : isGameStarted
                ? t('challengeAI.statusInGame')
                : t('challengeAI.statusReady')}
            </span>
          </div>
        </div>

        <div className={styles.navigationButtons}>
          {!isGameStarted && selectionMode === 'None' ? (
            <>
              {/* ONE Start button. Which opening it deals is the toggle above, not a
                  second button beside it. */}
              <button className={styles.newGameButton} onClick={startGameByPreference}>
                {t('challengeAI.startButton')}
              </button>
            </>
          ) : selectionMode === 'PieceSelection' ? (
            // Show New Game button during piece selection
            <button className={styles.resetButton} onClick={handleResetGame}>
              {t('challengeAI.newGameButton')}
            </button>
          ) : isGameStarted ? (
            <>
              <button
                className={isPaused ? styles.newGameButton : styles.randomButton}
                onClick={handlePauseToggle}
              >
                {isPaused
                  ? `▶ ${t('challengeAI.resumeButton')}`
                  : isPauseQueued
                    ? `⏸ ${t('challengeAI.pauseQueuedButton')}`
                    : `⏸ ${t('challengeAI.pauseButton')}`}
              </button>
              {/* View Kings toggle - only show for AI vs AI games */}
              {lightPlayer !== AIPlayerType.PLAYER && darkPlayer !== AIPlayerType.PLAYER && (
                <button
                  className={
                    showKingsQueued !== null
                      ? styles.randomButton // Show as "pending" style when queued
                      : showKings
                        ? styles.newGameButton
                        : styles.randomButton
                  }
                  onClick={() => {
                    const newShowKings = !showKings;

                    if (isAIThinking) {
                      // Queue the toggle for after AI finishes thinking
                      // INSTANT FEEDBACK: Set both the queued value AND the visual state immediately
                   
                      setShowKingsQueued(newShowKings);
                      setShowKings(newShowKings); // Visual feedback - show the change immediately

                      // Also update ref for visual consistency
                      if (challengeConfigRef.current) {
                        challengeConfigRef.current.showKings = newShowKings;
                      }
                    } else {
                      // Apply immediately
                     
                      setShowKings(newShowKings);

                      // CRITICAL: Update the ref FIRST, then update game state
                      if (challengeConfigRef.current) {
                        challengeConfigRef.current.showKings = newShowKings;

                        // Update the game state's challengeAIConfig
                        const currentGameState = ZodiacEngineService.zodiacEngine.getGameState();
                        (currentGameState as any).challengeAIConfig = {
                          ...challengeConfigRef.current,
                        };
                        updateGameState(currentGameState);
                      
                      }
                    }
                  }}
                  title={t('challengeAI.toggleKingVisibility')}
                >
                  {showKingsQueued !== null
                    ? `👁️ ${showKingsQueued ? t('challengeAI.hideKingsQueuedButton') : t('challengeAI.viewKingsQueuedButton')}`
                    : `👁️ ${showKings ? t('challengeAI.hideKingsButton') : t('challengeAI.viewKingsButton')}`}
                </button>
              )}
              <button className={styles.randomButton} onClick={handleRematch}>
                {/* Rematch: deals another game the way the toggle is set, so the label is
                    plain Start rather than the old Start Random. */}
                {t('challengeAI.startButton')}
              </button>
              <button className={styles.resetButton} onClick={handleResetGame}>
                {t('challengeAI.newGameButton')}
              </button>
            </>
          ) : null}
        </div>
        <AstraReview report={astraReport} />
      </div>

      {/* Warning Modal */}
      {warningMessage && (
        <div className={styles.modalOverlay}>
          <div className={styles.warningModal} onClick={(e) => e.stopPropagation()}>
            <h2>⚠️ {t('challengeAI.invalidPlacement')}</h2>
            <p>{warningMessage}</p>
            <button
              className={styles.modalButton}
              onClick={() => setWarningMessage(null)}
            >
              {t('challengeAI.okButton')}
            </button>
          </div>
        </div>
      )}

      {/* King Selection Modal */}
      {selectionMode === 'KingSelection' && (
        <KingSelectionModal
          isLight={isSelectingForLight}
          playerType={isSelectingForLight ? lightPlayer : darkPlayer}
          onKingSelected={handleKingSelected}
          showLookAwayWarning={lightPlayer === AIPlayerType.PLAYER && darkPlayer === AIPlayerType.PLAYER}
        />
      )}

      {/* Game Area */}
      <div className={styles.gameArea}>
        <div className={styles.gameAreaContent} style={{ position: 'relative' }}>
          {/* Local 2P: Left side panel for Light pieces */}
          {selectionMode === 'PieceSelection' && lightPlayer === AIPlayerType.PLAYER && darkPlayer === AIPlayerType.PLAYER && (
            <div className={styles.localPvPSidePanel}>
              <h4 className={styles.localPvPSidePanelLabel}>{t('challengeAI.lightPlayer')}</h4>
              <PieceSelectionPanel
                placedPieces={placedPieces}
                selectedKing={selectedLightKing}
                isLight={true}
                onPieceDragStart={handlePieceDragStart}
                hideKingIndicator={true}
                compact={true}
              />
            </div>
          )}

          <div className={styles.boardWithTimers}>
            {/* Dark Player Timer (above board) — hidden entirely when preset is OFF */}
            {isTimedPreset(activePreset) && (isGameStarted || gameState.state === State.Playing) && (
              <div className={styles.timerAboveBoard}>
                <TimerDisplay player="dark" isActive={gameState.isLightTurn === false} />
              </div>
            )}

            <div className={styles.boardContainer}>
              <Board
              gameState={gameState}
              uiState={{
                potentialMoves: potentialMoves,
                potentialSideEffects: potentialSideEffects,
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              highlightedRows={selectionMode === 'PieceSelection' && (Array.from(placedPieces).filter(k => k.endsWith('-light')).length < 5 || Array.from(placedPieces).filter(k => k.endsWith('-dark')).length < 5) ? {
                light: lightPlayer === AIPlayerType.PLAYER ? 0 : null,
                dark: darkPlayer === AIPlayerType.PLAYER ? 7 : null
              } : undefined}
            />
            </div>

            {/* Light Player Timer (below board) — hidden entirely when preset is OFF */}
            {isTimedPreset(activePreset) && (isGameStarted || gameState.state === State.Playing) && (
              <div className={styles.timerBelowBoard}>
                <TimerDisplay player="light" isActive={gameState.isLightTurn === true} />
              </div>
            )}

            {/* Piece Selection Panel - Show underneath the board during piece placement (non-Local 2P modes only) */}
            {selectionMode === 'PieceSelection' && (lightPlayer === AIPlayerType.PLAYER || darkPlayer === AIPlayerType.PLAYER) && !(lightPlayer === AIPlayerType.PLAYER && darkPlayer === AIPlayerType.PLAYER) && (
              <div className={styles.pieceSelectionPanel}>
                {renderPieceSelection()}
              </div>
            )}
          </div>

          {/* Local 2P: Right side panel for Dark pieces */}
          {selectionMode === 'PieceSelection' && lightPlayer === AIPlayerType.PLAYER && darkPlayer === AIPlayerType.PLAYER && (
            <div className={styles.localPvPSidePanel}>
              <h4 className={styles.localPvPSidePanelLabel}>{t('challengeAI.darkPlayer')}</h4>
              <PieceSelectionPanel
                placedPieces={placedPieces}
                selectedKing={selectedDarkKing}
                isLight={false}
                onPieceDragStart={handlePieceDragStart}
                hideKingIndicator={true}
                compact={true}
              />
            </div>
          )}

          {/* Right side panels with move history and game info.
              Mounted on desktop except during PieceSelection (where the wider piece tray
              would overlap the right panel). The Start Random flow never enters
              PieceSelection, so the board stays put during that transition. */}
          {selectionMode !== 'PieceSelection' && (
            <div className={styles.rightPanel}>
              <div className={styles.rightPanelColumn}>
                <FENHistory ref={fenHistoryRef} moveHistoryResetKey={moveHistoryResetKey} />
                <GameInfo />
              </div>
            </div>
          )}
        </div>
      </div>
      </div>

      {/* New-player welcome modal (one-time, triggered from MainPage PLAY NOW) */}
      <NewPlayerWelcomeModal
        isOpen={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
      />

      {/* Game End Popup */}
      <GameEndPopup
        isVisible={showGameEndPopup}
        result={gameEndResult}
        condition={gameEndCondition}
        raceKingType={gameEndRaceKingType}
        capturedKingType={gameEndCapturedKingType}
        capturedKingIsLight={gameEndCapturedKingIsLight}
        onClose={() => setShowGameEndPopup(false)}
        onRematch={handleRematch}
        onBackToMenu={() => { if (seriesActive) endSeries(); handleResetGame(); }}
      />
    </AdSenseLayout>
  );
}

export default ChallengeAI;
